// src/Profil/AdminUporabniki/AdminUporabniki.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../api";
import "./AdminUporabniki.css";

const zacetniObrazec = {
    ePosta: "",
    geslo: "",
    ponoviGeslo: "",
    ime: "",
    priimek: "",
    naslov: "",
    gsm: "",
    idZaDdv: "",
    maticnaStevilka: "",
    transakcijskiRacunStevilka: "",
    nazivBanke: ""
};

const zacetniReset = {
    odprto: false,
    id: "",
    ePosta: "",
    geslo: "",
    ponoviGeslo: ""
};

const zacetniUredi = {
    odprto: false,
    id: "",
    ePosta: "",
    ime: "",
    priimek: "",
    naslov: "",
    gsm: "",
    idZaDdv: "",
    maticnaStevilka: "",
    transakcijskiRacunStevilka: "",
    nazivBanke: ""
};

const zacetnoSporocilo = {
    text: "",
    type: "idle" // idle | success | error
};

function jeVeljavnaEPosta(value) {
    return /\S+@\S+\.\S+/.test((value || "").trim());
}

function jeMocnoGeslo(value) {
    const g = value || "";

    return (
        g.length >= 12 &&
        /[A-Z]/.test(g) &&
        /[a-z]/.test(g) &&
        /\d/.test(g) &&
        /[^A-Za-z0-9]/.test(g)
    );
}

function ocisti(value) {
    return (value || "").trim();
}

function formatirajDatum(value) {
    if (!value) return "—";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) {
        return "—";
    }

    return d.toLocaleDateString("sl-SI");
}

async function preberiSporociloNapake(res, privzeto) {
    try {
        const data = await res.json();

        if (data?.message) return data.message;
        if (data?.title) return data.title;

        if (data?.errors && typeof data.errors === "object") {
            const prvaNapaka = Object.values(data.errors)
                .flat()
                .find(Boolean);

            if (prvaNapaka) return String(prvaNapaka);
        }
    } catch {
        // ignore
    }

    return privzeto;
}

function normalizirajUporabnika(u) {
    return {
        id: u?.id || "",
        ePosta: u?.ePosta || "",
        vloga: u?.vloga || "",
        ime: u?.ime || "",
        priimek: u?.priimek || "",
        naslov: u?.naslov || "",
        gsm: u?.gsm || "",
        idZaDdv: u?.idZaDdv || "",
        maticnaStevilka: u?.maticnaStevilka || "",
        transakcijskiRacunStevilka: u?.transakcijskiRacunStevilka || "",
        nazivBanke: u?.nazivBanke || "",
        ustvarjen: u?.ustvarjen || ""
    };
}

export default function AdminUporabniki() {
    const navigate = useNavigate();

    const [obrazec, setObrazec] = useState(zacetniObrazec);
    const [uporabniki, setUporabniki] = useState([]);

    const [naloziStatus, setNaloziStatus] = useState("idle");
    const [ustvariStatus, setUstvariStatus] = useState("idle");
    const [sporocilo, setSporocilo] = useState(zacetnoSporocilo);

    const [pokaziGesli, setPokaziGesli] = useState(false);

    const [reset, setReset] = useState(zacetniReset);
    const [resetStatus, setResetStatus] = useState("idle");
    const [resetSporocilo, setResetSporocilo] = useState("");
    const [pokaziResetGesli, setPokaziResetGesli] = useState(false);

    const [uredi, setUredi] = useState(zacetniUredi);
    const [urediStatus, setUrediStatus] = useState("idle");
    const [urediSporocilo, setUrediSporocilo] = useState("");

    const emailVeljaven = useMemo(
        () => jeVeljavnaEPosta(obrazec.ePosta),
        [obrazec.ePosta]
    );

    const gesloVeljavno = useMemo(
        () => jeMocnoGeslo(obrazec.geslo),
        [obrazec.geslo]
    );

    const gesliEnaki = obrazec.geslo === obrazec.ponoviGeslo;

    const lahkoShranim =
        emailVeljaven &&
        gesloVeljavno &&
        gesliEnaki &&
        ustvariStatus !== "loading";

    const resetGesloVeljavno = useMemo(
        () => jeMocnoGeslo(reset.geslo),
        [reset.geslo]
    );

    const resetGesliEnaki = reset.geslo === reset.ponoviGeslo;

    const lahkoResetiram =
        reset.odprto &&
        resetGesloVeljavno &&
        resetGesliEnaki &&
        resetStatus !== "loading";

    const lahkoUredim = uredi.odprto && urediStatus !== "loading";

    const naloziUporabnike = useCallback(async () => {
        setNaloziStatus("loading");
        setSporocilo(zacetnoSporocilo);

        try {
            const res = await apiFetch("/api/admin/uporabniki");

            if (res.status === 403) {
                setNaloziStatus("error");
                setSporocilo({
                    type: "error",
                    text: "Nimate pravic za upravljanje uporabnikov."
                });
                return;
            }

            if (!res.ok) {
                const text = await preberiSporociloNapake(
                    res,
                    "Uporabnikov ni bilo mogoče naložiti."
                );

                setNaloziStatus("error");
                setSporocilo({
                    type: "error",
                    text
                });
                return;
            }

            const data = await res.json();
            const seznam = Array.isArray(data)
                ? data.map(normalizirajUporabnika)
                : [];

            setUporabniki(seznam);
            setNaloziStatus("success");
        } catch {
            setNaloziStatus("error");
            setSporocilo({
                type: "error",
                text: "Napaka pri povezavi s strežnikom."
            });
        }
    }, []);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto"
        });

        naloziUporabnike();
    }, [naloziUporabnike]);

    function spremeni(polje, vrednost) {
        setObrazec((o) => ({
            ...o,
            [polje]: vrednost
        }));
    }

    async function shraniUporabnika(e) {
        e.preventDefault();

        if (!lahkoShranim) {
            setUstvariStatus("error");

            if (!emailVeljaven) {
                setSporocilo({
                    type: "error",
                    text: "Vnesite veljaven e-poštni naslov."
                });
            } else if (!gesloVeljavno) {
                setSporocilo({
                    type: "error",
                    text: "Geslo mora imeti vsaj 12 znakov ter veliko črko, malo črko, številko in poseben znak."
                });
            } else if (!gesliEnaki) {
                setSporocilo({
                    type: "error",
                    text: "Gesli se ne ujemata."
                });
            }

            return;
        }

        setUstvariStatus("loading");
        setSporocilo(zacetnoSporocilo);

        try {
            const res = await apiFetch("/api/admin/uporabniki", {
                method: "POST",
                body: {
                    ePosta: ocisti(obrazec.ePosta),
                    geslo: obrazec.geslo,
                    ponoviGeslo: obrazec.ponoviGeslo,
                    ime: ocisti(obrazec.ime),
                    priimek: ocisti(obrazec.priimek),
                    naslov: ocisti(obrazec.naslov),
                    gsm: ocisti(obrazec.gsm),
                    idZaDdv: ocisti(obrazec.idZaDdv),
                    maticnaStevilka: ocisti(obrazec.maticnaStevilka),
                    transakcijskiRacunStevilka: ocisti(
                        obrazec.transakcijskiRacunStevilka
                    ),
                    nazivBanke: ocisti(obrazec.nazivBanke)
                }
            });

            if (!res.ok) {
                const text = await preberiSporociloNapake(
                    res,
                    "Uporabnika ni bilo mogoče ustvariti."
                );

                setUstvariStatus("error");
                setSporocilo({
                    type: "error",
                    text
                });
                return;
            }

            const nov = normalizirajUporabnika(await res.json());

            setUporabniki((seznam) => [nov, ...seznam]);
            setObrazec(zacetniObrazec);
            setPokaziGesli(false);
            setUstvariStatus("success");
            setSporocilo({
                type: "success",
                text: "Uporabnik je bil uspešno ustvarjen."
            });
        } catch {
            setUstvariStatus("error");
            setSporocilo({
                type: "error",
                text: "Napaka pri povezavi s strežnikom."
            });
        }
    }

    function odpriUrejanje(uporabnik) {
        setUredi({
            odprto: true,
            id: uporabnik.id || "",
            ePosta: uporabnik.ePosta || "",
            ime: uporabnik.ime || "",
            priimek: uporabnik.priimek || "",
            naslov: uporabnik.naslov || "",
            gsm: uporabnik.gsm || "",
            idZaDdv: uporabnik.idZaDdv || "",
            maticnaStevilka: uporabnik.maticnaStevilka || "",
            transakcijskiRacunStevilka:
                uporabnik.transakcijskiRacunStevilka || "",
            nazivBanke: uporabnik.nazivBanke || ""
        });

        setUrediStatus("idle");
        setUrediSporocilo("");
    }

    function zapriUrejanje() {
        if (urediStatus === "loading") return;

        setUredi(zacetniUredi);
        setUrediStatus("idle");
        setUrediSporocilo("");
    }

    function spremeniUredi(polje, vrednost) {
        setUredi((u) => ({
            ...u,
            [polje]: vrednost
        }));
    }

    async function shraniUrejanje(e) {
        e.preventDefault();

        if (!uredi.id) {
            setUrediStatus("error");
            setUrediSporocilo("ID uporabnika manjka.");
            return;
        }

        setUrediStatus("loading");
        setUrediSporocilo("");

        try {
            const res = await apiFetch(
                `/api/admin/uporabniki/${encodeURIComponent(uredi.id)}`,
                {
                    method: "PUT",
                    body: {
                        ime: ocisti(uredi.ime),
                        priimek: ocisti(uredi.priimek),
                        naslov: ocisti(uredi.naslov),
                        gsm: ocisti(uredi.gsm),
                        idZaDdv: ocisti(uredi.idZaDdv),
                        maticnaStevilka: ocisti(uredi.maticnaStevilka),
                        transakcijskiRacunStevilka: ocisti(
                            uredi.transakcijskiRacunStevilka
                        ),
                        nazivBanke: ocisti(uredi.nazivBanke)
                    }
                }
            );

            if (!res.ok) {
                const text = await preberiSporociloNapake(
                    res,
                    "Uporabnika ni bilo mogoče posodobiti."
                );

                setUrediStatus("error");
                setUrediSporocilo(text);
                return;
            }

            const posodobljen = normalizirajUporabnika(await res.json());

            setUporabniki((seznam) =>
                seznam.map((u) =>
                    u.id === posodobljen.id ? posodobljen : u
                )
            );

            setUredi(zacetniUredi);
            setUrediStatus("idle");
            setUrediSporocilo("");

            setSporocilo({
                type: "success",
                text: "Uporabnik je bil uspešno posodobljen."
            });
        } catch {
            setUrediStatus("error");
            setUrediSporocilo("Napaka pri povezavi s strežnikom.");
        }
    }

    function odpriResetGesla(uporabnik) {
        setReset({
            odprto: true,
            id: uporabnik.id || "",
            ePosta: uporabnik.ePosta || "",
            geslo: "",
            ponoviGeslo: ""
        });

        setPokaziResetGesli(false);
        setResetStatus("idle");
        setResetSporocilo("");
    }

    function zapriResetGesla() {
        if (resetStatus === "loading") return;

        setReset(zacetniReset);
        setPokaziResetGesli(false);
        setResetStatus("idle");
        setResetSporocilo("");
    }

    function spremeniReset(polje, vrednost) {
        setReset((r) => ({
            ...r,
            [polje]: vrednost
        }));
    }

    async function ponastaviGeslo(e) {
        e.preventDefault();

        if (!lahkoResetiram) {
            setResetStatus("error");

            if (!resetGesloVeljavno) {
                setResetSporocilo(
                    "Novo geslo mora imeti vsaj 12 znakov ter veliko črko, malo črko, številko in poseben znak."
                );
            } else if (!resetGesliEnaki) {
                setResetSporocilo("Gesli se ne ujemata.");
            }

            return;
        }

        setResetStatus("loading");
        setResetSporocilo("");

        try {
            const res = await apiFetch(
                `/api/admin/uporabniki/${encodeURIComponent(reset.id)}/geslo`,
                {
                    method: "POST",
                    body: {
                        geslo: reset.geslo,
                        ponoviGeslo: reset.ponoviGeslo
                    }
                }
            );

            if (!res.ok) {
                const text = await preberiSporociloNapake(
                    res,
                    "Gesla ni bilo mogoče ponastaviti."
                );

                setResetStatus("error");
                setResetSporocilo(text);
                return;
            }

            setReset(zacetniReset);
            setPokaziResetGesli(false);
            setResetStatus("idle");
            setResetSporocilo("");

            setSporocilo({
                type: "success",
                text: "Geslo uporabnika je bilo uspešno ponastavljeno."
            });
        } catch {
            setResetStatus("error");
            setResetSporocilo("Napaka pri povezavi s strežnikom.");
        }
    }

    return (
        <div className="au-page">
            <section className="au-card">
                <div className="au-top">
                    <div className="au-topText">
                        <p className="au-kicker">Administracija</p>
                        <h1 className="au-title">Uporabniki</h1>
                        <p className="au-subtitle">
                            Tukaj admin neposredno ustvari uporabnika. Javne registracije ni.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="au-backButton"
                        onClick={() => navigate("/profil")}
                    >
                        Nazaj
                    </button>
                </div>

                <form className="au-form" onSubmit={shraniUporabnika} noValidate>
                    <div className="au-grid">
                        <div className="au-field au-field--wide">
                            <label htmlFor="au-eposta">E-pošta *</label>
                            <input
                                id="au-eposta"
                                type="email"
                                value={obrazec.ePosta}
                                onChange={(e) =>
                                    spremeni("ePosta", e.target.value)
                                }
                                placeholder="uporabnik@email.si"
                                autoComplete="username"
                                required
                            />
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-geslo">Geslo *</label>
                            <div className="au-passwordWrap">
                                <input
                                    id="au-geslo"
                                    type={pokaziGesli ? "text" : "password"}
                                    value={obrazec.geslo}
                                    onChange={(e) =>
                                        spremeni("geslo", e.target.value)
                                    }
                                    placeholder="Najmanj 12 znakov"
                                    autoComplete="new-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="au-passwordButton"
                                    onClick={() => setPokaziGesli((v) => !v)}
                                    aria-label={
                                        pokaziGesli
                                            ? "Skrij geslo"
                                            : "Pokaži geslo"
                                    }
                                    title={
                                        pokaziGesli
                                            ? "Skrij geslo"
                                            : "Pokaži geslo"
                                    }
                                >
                                    {pokaziGesli ? "Skrij" : "Pokaži"}
                                </button>
                            </div>
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-ponovi-geslo">
                                Ponovi geslo *
                            </label>
                            <div className="au-passwordWrap">
                                <input
                                    id="au-ponovi-geslo"
                                    type={pokaziGesli ? "text" : "password"}
                                    value={obrazec.ponoviGeslo}
                                    onChange={(e) =>
                                        spremeni("ponoviGeslo", e.target.value)
                                    }
                                    placeholder="Ponovite geslo"
                                    autoComplete="new-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="au-passwordButton"
                                    onClick={() => setPokaziGesli((v) => !v)}
                                    aria-label={
                                        pokaziGesli
                                            ? "Skrij geslo"
                                            : "Pokaži geslo"
                                    }
                                    title={
                                        pokaziGesli
                                            ? "Skrij geslo"
                                            : "Pokaži geslo"
                                    }
                                >
                                    {pokaziGesli ? "Skrij" : "Pokaži"}
                                </button>
                            </div>
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-ime">Ime</label>
                            <input
                                id="au-ime"
                                type="text"
                                value={obrazec.ime}
                                onChange={(e) =>
                                    spremeni("ime", e.target.value)
                                }
                                autoComplete="given-name"
                            />
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-priimek">Priimek</label>
                            <input
                                id="au-priimek"
                                type="text"
                                value={obrazec.priimek}
                                onChange={(e) =>
                                    spremeni("priimek", e.target.value)
                                }
                                autoComplete="family-name"
                            />
                        </div>

                        <div className="au-field au-field--wide">
                            <label htmlFor="au-naslov">Naslov</label>
                            <input
                                id="au-naslov"
                                type="text"
                                value={obrazec.naslov}
                                onChange={(e) =>
                                    spremeni("naslov", e.target.value)
                                }
                                autoComplete="street-address"
                            />
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-gsm">GSM</label>
                            <input
                                id="au-gsm"
                                type="text"
                                value={obrazec.gsm}
                                onChange={(e) =>
                                    spremeni("gsm", e.target.value)
                                }
                                autoComplete="tel"
                            />
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-id-ddv">ID za DDV</label>
                            <input
                                id="au-id-ddv"
                                type="text"
                                value={obrazec.idZaDdv}
                                onChange={(e) =>
                                    spremeni("idZaDdv", e.target.value)
                                }
                                placeholder="SI12345678"
                                autoCapitalize="characters"
                            />
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-maticna">
                                Matična številka
                            </label>
                            <input
                                id="au-maticna"
                                type="text"
                                value={obrazec.maticnaStevilka}
                                onChange={(e) =>
                                    spremeni("maticnaStevilka", e.target.value)
                                }
                                inputMode="numeric"
                            />
                        </div>

                        <div className="au-field au-field--wide">
                            <label htmlFor="au-trr">
                                Transakcijski račun št.
                            </label>
                            <input
                                id="au-trr"
                                type="text"
                                value={obrazec.transakcijskiRacunStevilka}
                                onChange={(e) =>
                                    spremeni(
                                        "transakcijskiRacunStevilka",
                                        e.target.value
                                    )
                                }
                                placeholder="SI56..."
                                autoCapitalize="characters"
                            />
                        </div>

                        <div className="au-field au-field--wide">
                            <label htmlFor="au-naziv-banke">
                                Naziv banke
                            </label>
                            <input
                                id="au-naziv-banke"
                                type="text"
                                value={obrazec.nazivBanke}
                                onChange={(e) =>
                                    spremeni("nazivBanke", e.target.value)
                                }
                                placeholder="npr. NLB d.d."
                            />
                        </div>
                    </div>

                    <div className="au-actions">
                        <button
                            type="submit"
                            className="au-submitButton"
                            disabled={!lahkoShranim}
                        >
                            {ustvariStatus === "loading"
                                ? "Ustvarjanje ..."
                                : "Ustvari uporabnika"}
                        </button>
                    </div>

                    {sporocilo.text && (
                        <div
                            className={`au-message au-message--${sporocilo.type}`}
                            role="status"
                            aria-live="polite"
                        >
                            {sporocilo.text}
                        </div>
                    )}
                </form>
            </section>

            <section className="au-card">
                <div className="au-listHead">
                    <div className="au-listHeadText">
                        <p className="au-kicker">Seznam</p>
                        <h2 className="au-title au-title--small">
                            Obstoječi uporabniki
                        </h2>
                    </div>

                    <button
                        type="button"
                        className="au-refreshButton"
                        onClick={naloziUporabnike}
                        disabled={naloziStatus === "loading"}
                    >
                        {naloziStatus === "loading" ? "Nalagam ..." : "Osveži"}
                    </button>
                </div>

                <div className="au-tableWrap">
                    <table className="au-table">
                        <thead>
                            <tr>
                                <th>E-pošta</th>
                                <th>Ime</th>
                                <th>Vloga</th>
                                <th>GSM</th>
                                <th>ID za DDV</th>
                                <th>Matična št.</th>
                                <th>TRR</th>
                                <th>Banka</th>
                                <th>Uredi</th>
                                <th>Geslo</th>
                                <th>Ustvarjen</th>
                            </tr>
                        </thead>

                        <tbody>
                            {uporabniki.length === 0 ? (
                                <tr>
                                    <td colSpan="11" className="au-empty">
                                        {naloziStatus === "loading"
                                            ? "Nalaganje uporabnikov ..."
                                            : "Ni uporabnikov."}
                                    </td>
                                </tr>
                            ) : (
                                uporabniki.map((u) => (
                                    <tr key={u.id}>
                                        <td>{u.ePosta}</td>

                                        <td>
                                            {[u.ime, u.priimek]
                                                .filter(Boolean)
                                                .join(" ") || "—"}
                                        </td>

                                        <td>
                                            <span
                                                className={
                                                    u.vloga === "Admin"
                                                        ? "au-role au-role--admin"
                                                        : "au-role"
                                                }
                                            >
                                                {u.vloga}
                                            </span>
                                        </td>

                                        <td>{u.gsm || "—"}</td>
                                        <td>{u.idZaDdv || "—"}</td>
                                        <td>{u.maticnaStevilka || "—"}</td>
                                        <td>
                                            {u.transakcijskiRacunStevilka ||
                                                "—"}
                                        </td>
                                        <td>{u.nazivBanke || "—"}</td>

                                        <td>
                                            <button
                                                type="button"
                                                className="au-smallButton"
                                                onClick={() => odpriUrejanje(u)}
                                            >
                                                Uredi
                                            </button>
                                        </td>

                                        <td>
                                            <button
                                                type="button"
                                                className="au-smallButton"
                                                onClick={() => odpriResetGesla(u)}
                                            >
                                                Reset gesla
                                            </button>
                                        </td>

                                        <td>{formatirajDatum(u.ustvarjen)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {uredi.odprto && (
                <div
                    className="au-resetOverlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="au-edit-title"
                >
                    <form
                        className="au-resetCard"
                        onSubmit={shraniUrejanje}
                        noValidate
                    >
                        <div className="au-resetHead">
                            <div className="au-resetHeadText">
                                <p className="au-kicker">Urejanje uporabnika</p>
                                <h2
                                    id="au-edit-title"
                                    className="au-title au-title--small"
                                >
                                    {uredi.ePosta}
                                </h2>
                            </div>

                            <button
                                type="button"
                                className="au-resetClose"
                                onClick={zapriUrejanje}
                                aria-label="Zapri"
                                disabled={urediStatus === "loading"}
                            >
                                ×
                            </button>
                        </div>

                        <div className="au-grid">
                            <div className="au-field">
                                <label htmlFor="au-edit-ime">Ime</label>
                                <input
                                    id="au-edit-ime"
                                    type="text"
                                    value={uredi.ime}
                                    onChange={(e) =>
                                        spremeniUredi("ime", e.target.value)
                                    }
                                    autoComplete="given-name"
                                />
                            </div>

                            <div className="au-field">
                                <label htmlFor="au-edit-priimek">Priimek</label>
                                <input
                                    id="au-edit-priimek"
                                    type="text"
                                    value={uredi.priimek}
                                    onChange={(e) =>
                                        spremeniUredi("priimek", e.target.value)
                                    }
                                    autoComplete="family-name"
                                />
                            </div>

                            <div className="au-field au-field--wide">
                                <label htmlFor="au-edit-naslov">Naslov</label>
                                <input
                                    id="au-edit-naslov"
                                    type="text"
                                    value={uredi.naslov}
                                    onChange={(e) =>
                                        spremeniUredi("naslov", e.target.value)
                                    }
                                    autoComplete="street-address"
                                />
                            </div>

                            <div className="au-field">
                                <label htmlFor="au-edit-gsm">GSM</label>
                                <input
                                    id="au-edit-gsm"
                                    type="text"
                                    value={uredi.gsm}
                                    onChange={(e) =>
                                        spremeniUredi("gsm", e.target.value)
                                    }
                                    autoComplete="tel"
                                />
                            </div>

                            <div className="au-field">
                                <label htmlFor="au-edit-ddv">ID za DDV</label>
                                <input
                                    id="au-edit-ddv"
                                    type="text"
                                    value={uredi.idZaDdv}
                                    onChange={(e) =>
                                        spremeniUredi("idZaDdv", e.target.value)
                                    }
                                    autoCapitalize="characters"
                                />
                            </div>

                            <div className="au-field">
                                <label htmlFor="au-edit-maticna">
                                    Matična številka
                                </label>
                                <input
                                    id="au-edit-maticna"
                                    type="text"
                                    value={uredi.maticnaStevilka}
                                    onChange={(e) =>
                                        spremeniUredi(
                                            "maticnaStevilka",
                                            e.target.value
                                        )
                                    }
                                    inputMode="numeric"
                                />
                            </div>

                            <div className="au-field au-field--wide">
                                <label htmlFor="au-edit-trr">
                                    Transakcijski račun št.
                                </label>
                                <input
                                    id="au-edit-trr"
                                    type="text"
                                    value={uredi.transakcijskiRacunStevilka}
                                    onChange={(e) =>
                                        spremeniUredi(
                                            "transakcijskiRacunStevilka",
                                            e.target.value
                                        )
                                    }
                                    autoCapitalize="characters"
                                />
                            </div>

                            <div className="au-field au-field--wide">
                                <label htmlFor="au-edit-naziv-banke">
                                    Naziv banke
                                </label>
                                <input
                                    id="au-edit-naziv-banke"
                                    type="text"
                                    value={uredi.nazivBanke}
                                    onChange={(e) =>
                                        spremeniUredi(
                                            "nazivBanke",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>

                        {urediSporocilo && (
                            <div
                                className={`au-message au-message--${urediStatus}`}
                                role="status"
                                aria-live="polite"
                            >
                                {urediSporocilo}
                            </div>
                        )}

                        <div className="au-resetActions">
                            <button
                                type="button"
                                className="au-refreshButton"
                                onClick={zapriUrejanje}
                                disabled={urediStatus === "loading"}
                            >
                                Prekliči
                            </button>

                            <button
                                type="submit"
                                className="au-submitButton"
                                disabled={!lahkoUredim}
                            >
                                {urediStatus === "loading"
                                    ? "Shranjevanje ..."
                                    : "Shrani"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {reset.odprto && (
                <div
                    className="au-resetOverlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="au-reset-title"
                >
                    <form
                        className="au-resetCard"
                        onSubmit={ponastaviGeslo}
                        noValidate
                    >
                        <div className="au-resetHead">
                            <div className="au-resetHeadText">
                                <p className="au-kicker">Ponastavitev gesla</p>
                                <h2
                                    id="au-reset-title"
                                    className="au-title au-title--small"
                                >
                                    {reset.ePosta}
                                </h2>
                            </div>

                            <button
                                type="button"
                                className="au-resetClose"
                                onClick={zapriResetGesla}
                                aria-label="Zapri"
                                disabled={resetStatus === "loading"}
                            >
                                ×
                            </button>
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-reset-geslo">Novo geslo *</label>
                            <div className="au-passwordWrap">
                                <input
                                    id="au-reset-geslo"
                                    type={pokaziResetGesli ? "text" : "password"}
                                    value={reset.geslo}
                                    onChange={(e) =>
                                        spremeniReset("geslo", e.target.value)
                                    }
                                    placeholder="Najmanj 12 znakov"
                                    autoComplete="new-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="au-passwordButton"
                                    onClick={() =>
                                        setPokaziResetGesli((v) => !v)
                                    }
                                    aria-label={
                                        pokaziResetGesli
                                            ? "Skrij novo geslo"
                                            : "Pokaži novo geslo"
                                    }
                                    title={
                                        pokaziResetGesli
                                            ? "Skrij novo geslo"
                                            : "Pokaži novo geslo"
                                    }
                                >
                                    {pokaziResetGesli ? "Skrij" : "Pokaži"}
                                </button>
                            </div>
                        </div>

                        <div className="au-field">
                            <label htmlFor="au-reset-ponovi">
                                Ponovi novo geslo *
                            </label>
                            <div className="au-passwordWrap">
                                <input
                                    id="au-reset-ponovi"
                                    type={pokaziResetGesli ? "text" : "password"}
                                    value={reset.ponoviGeslo}
                                    onChange={(e) =>
                                        spremeniReset(
                                            "ponoviGeslo",
                                            e.target.value
                                        )
                                    }
                                    placeholder="Ponovite novo geslo"
                                    autoComplete="new-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="au-passwordButton"
                                    onClick={() =>
                                        setPokaziResetGesli((v) => !v)
                                    }
                                    aria-label={
                                        pokaziResetGesli
                                            ? "Skrij novo geslo"
                                            : "Pokaži novo geslo"
                                    }
                                    title={
                                        pokaziResetGesli
                                            ? "Skrij novo geslo"
                                            : "Pokaži novo geslo"
                                    }
                                >
                                    {pokaziResetGesli ? "Skrij" : "Pokaži"}
                                </button>
                            </div>
                        </div>

                        {resetSporocilo && (
                            <div
                                className={`au-message au-message--${resetStatus}`}
                                role="status"
                                aria-live="polite"
                            >
                                {resetSporocilo}
                            </div>
                        )}

                        <div className="au-resetActions">
                            <button
                                type="button"
                                className="au-refreshButton"
                                onClick={zapriResetGesla}
                                disabled={resetStatus === "loading"}
                            >
                                Prekliči
                            </button>

                            <button
                                type="submit"
                                className="au-submitButton"
                                disabled={!lahkoResetiram}
                            >
                                {resetStatus === "loading"
                                    ? "Shranjevanje ..."
                                    : "Ponastavi geslo"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}